import { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Button, Text, Badge, VStack, HStack, Alert, AlertIcon,
  Spinner, Flex, Table, Thead, Tbody, Tr, Th, Td, Textarea,
  useToast, Image, Tabs, TabList, TabPanels, Tab, TabPanel,
  Switch, Input, InputGroup, InputLeftElement, RadioGroup, Radio, Stack, Divider,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { apiService } from '../services/apiService';
import MemberSelect from '../components/MemberSelect';

export default function WhatsAppAdmin() {
  const toast = useToast();
  const [statut, setStatut] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [abonnes, setAbonnes] = useState([]);
  const [membres, setMembres] = useState([]);
  const [membresAvecTel, setMembresAvecTel] = useState([]);
  const [modeDiffusion, setModeDiffusion] = useState('lecture');
  const [message, setMessage] = useState('');
  const [messageLibre, setMessageLibre] = useState('');
  const [lectureJour, setLectureJour] = useState(null);
  const [loadingLecture, setLoadingLecture] = useState(false);
  const [messageIndividuel, setMessageIndividuel] = useState('');
  const [membreSelectionne, setMembreSelectionne] = useState('');
  const [sending, setSending] = useState(false);
  const [sendingIndividuel, setSendingIndividuel] = useState(false);
  const [loadingStatut, setLoadingStatut] = useState(true);
  const [loadingQr, setLoadingQr] = useState(false);
  const [savingAbonnements, setSavingAbonnements] = useState(false);
  const [searchAbonnes, setSearchAbonnes] = useState('');
  const [pendingChanges, setPendingChanges] = useState({});

  // Anniversaires
  const [abonnesAnniversaires, setAbonnesAnniversaires] = useState([]);
  const [anniversairesDuJour, setAnniversairesDuJour] = useState([]);
  const [apercuMessageAnniv, setApercuMessageAnniv] = useState('');
  const [searchAbonnesAnniv, setSearchAbonnesAnniv] = useState('');
  const [pendingChangesAnniv, setPendingChangesAnniv] = useState({});
  const [savingAbonnementsAnniv, setSavingAbonnementsAnniv] = useState(false);
  const [envoyantAnniv, setEnvoyantAnniv] = useState(false);

  const fetchStatut = useCallback(async () => {
    try {
      const data = await apiService.makeRequest('/whatsapp/statut');
      setStatut(data.data);
      if (data.data?.pret) setQrCode(null);
    } catch {
      setStatut({ etat: 'DISCONNECTED', pret: false });
    } finally {
      setLoadingStatut(false);
    }
  }, []);

  const fetchQrCode = useCallback(async () => {
    try {
      setLoadingQr(true);
      const data = await apiService.makeRequest('/whatsapp/qrcode');
      setQrCode(data.data?.qrCode || null);
    } catch {} finally {
      setLoadingQr(false);
    }
  }, []);

  const fetchAbonnes = useCallback(async () => {
    try {
      const data = await apiService.makeRequest('/whatsapp/abonnes-lectures');
      setAbonnes(data.data?.membres || []);
    } catch {}
  }, []);

  const fetchMembres = useCallback(async () => {
    try {
      const data = await apiService.makeRequest('/whatsapp/membres');
      const all = data.data?.membres || [];
      setMembres(all);
      setMembresAvecTel(all);
      setPendingChanges({});
    } catch {}
  }, []);

  const fetchAbonnesAnniversaires = useCallback(async () => {
    try {
      const data = await apiService.makeRequest('/whatsapp/abonnes-anniversaires');
      setAbonnesAnniversaires(data.data?.membres || []);
      setPendingChangesAnniv({});
    } catch {}
  }, []);

  const fetchAnniversairesDuJour = useCallback(async () => {
    try {
      const data = await apiService.makeRequest('/whatsapp/anniversaires-du-jour');
      setAnniversairesDuJour(data.data?.membres || []);
    } catch {}
  }, []);

  const fetchApercuMessageAnniv = useCallback(async () => {
    try {
      const data = await apiService.makeRequest('/whatsapp/apercu-message-anniversaire');
      setApercuMessageAnniv(data.data?.message || '');
    } catch {}
  }, []);

  useEffect(() => {
    fetchStatut();
    fetchAbonnes();
    fetchMembres();
    fetchAbonnesAnniversaires();
    fetchAnniversairesDuJour();
    fetchApercuMessageAnniv();
    const interval = setInterval(fetchStatut, 10000);
    return () => clearInterval(interval);
  }, [fetchStatut, fetchAbonnes, fetchMembres, fetchAbonnesAnniversaires, fetchAnniversairesDuJour, fetchApercuMessageAnniv]);

  useEffect(() => {
    if (statut && !statut.pret && !statut.isReady) {
      fetchQrCode();
      const qrInterval = setInterval(fetchQrCode, 15000);
      return () => clearInterval(qrInterval);
    }
  }, [statut?.pret, fetchQrCode]);

  const handleDeconnecter = async () => {
    await apiService.makeRequest('/whatsapp/deconnecter', { method: 'POST' });
    toast({ title: 'WhatsApp déconnecté', status: 'info', duration: 3000 });
    fetchStatut();
  };

  const handleEnvoyerLecture = async () => {
    if (!message.trim()) return;
    try {
      setSending(true);
      const data = await apiService.makeRequest('/whatsapp/envoyer-lecture', {
        method: 'POST',
        body: JSON.stringify({ message }),
      });
      toast({ title: 'Envoi terminé', description: data.message, status: 'success', duration: 5000, isClosable: true });
      setMessage('');
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setSending(false);
    }
  };

  const fetchLectureJour = useCallback(async () => {
    try {
      setLoadingLecture(true);
      const data = await apiService.makeRequest('/whatsapp/lecture-du-jour');
      if (data.success) {
        setLectureJour(data.data);
        setMessage(data.data.message);
      } else {
        setLectureJour(null);
        setMessage('');
        toast({ title: 'Aucune lecture aujourd\'hui', description: data.message, status: 'warning', duration: 4000 });
      }
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 3000 });
    } finally {
      setLoadingLecture(false);
    }
  }, [toast]);

  const handleEnvoyerMessageLibre = async () => {
    if (!messageLibre.trim()) return;
    try {
      setSending(true);
      const data = await apiService.makeRequest('/whatsapp/envoyer-lecture', {
        method: 'POST',
        body: JSON.stringify({ message: messageLibre }),
      });
      toast({ title: 'Envoi terminé', description: data.message, status: 'success', duration: 5000, isClosable: true });
      setMessageLibre('');
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setSending(false);
    }
  };

  const handleEnvoyerIndividuel = async () => {
    if (!messageIndividuel.trim() || !membreSelectionne) return;
    const membre = membres.find(m => String(m.id) === String(membreSelectionne));
    if (!membre) return;
    try {
      setSendingIndividuel(true);
      await apiService.makeRequest('/whatsapp/envoyer-individuel', {
        method: 'POST',
        body: JSON.stringify({ telephone: membre.telephone, message: messageIndividuel }),
      });
      toast({ title: 'Message envoyé', description: `Message envoyé à ${membre.prenom} ${membre.nom}`, status: 'success', duration: 4000, isClosable: true });
      setMessageIndividuel('');
      setMembreSelectionne('');
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setSendingIndividuel(false);
    }
  };

  const getActif = (m) => {
    if (m.id in pendingChanges) return pendingChanges[m.id];
    return !!m.recevoir_lectures_whatsapp;
  };

  const handleToggle = (id, actif) => {
    setPendingChanges(prev => ({ ...prev, [id]: actif }));
  };

  const handleToutCocher = (actif) => {
    const changes = {};
    membresFiltresAbonnes.forEach(m => { changes[m.id] = actif; });
    setPendingChanges(prev => ({ ...prev, ...changes }));
  };

  const handleSaveAbonnements = async () => {
    const abonnements = Object.entries(pendingChanges).map(([id, actif]) => ({ id: Number(id), actif }));
    if (abonnements.length === 0) return;
    try {
      setSavingAbonnements(true);
      const data = await apiService.makeRequest('/whatsapp/abonnements', {
        method: 'POST',
        body: JSON.stringify({ abonnements }),
      });
      toast({ title: 'Abonnements enregistrés', description: data.message, status: 'success', duration: 3000 });
      await fetchMembres();
      await fetchAbonnes();
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setSavingAbonnements(false);
    }
  };

  const getActifAnniv = (m) => {
    if (m.id in pendingChangesAnniv) return pendingChangesAnniv[m.id];
    return !!m.recevoir_anniversaire_whatsapp;
  };

  const handleToggleAnniv = (id, actif) => {
    setPendingChangesAnniv(prev => ({ ...prev, [id]: actif }));
  };

  const handleToutCocherAnniv = (actif) => {
    const changes = {};
    membresFiltresAnniv.forEach(m => { changes[m.id] = actif; });
    setPendingChangesAnniv(prev => ({ ...prev, ...changes }));
  };

  const handleSaveAbonnementsAnniv = async () => {
    const abonnements = Object.entries(pendingChangesAnniv).map(([id, actif]) => ({ id: Number(id), actif }));
    if (abonnements.length === 0) return;
    try {
      setSavingAbonnementsAnniv(true);
      const data = await apiService.makeRequest('/whatsapp/abonnements-anniversaires', {
        method: 'POST',
        body: JSON.stringify({ abonnements }),
      });
      toast({ title: 'Abonnements enregistrés', description: data.message, status: 'success', duration: 3000 });
      await fetchAbonnesAnniversaires();
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setSavingAbonnementsAnniv(false);
    }
  };

  const handleEnvoyerAnniversairesMaintenant = async () => {
    try {
      setEnvoyantAnniv(true);
      const data = await apiService.makeRequest('/whatsapp/envoyer-anniversaires-maintenant', { method: 'POST' });
      const r = data.data;
      toast({
        title: 'Envoi terminé',
        description: r.skipped ? r.reason : `${r.nb_succes} succès, ${r.nb_echecs} échec(s), ${r.nb_deja_envoyes} déjà envoyé(s)`,
        status: 'success', duration: 5000, isClosable: true,
      });
      await fetchAnniversairesDuJour();
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setEnvoyantAnniv(false);
    }
  };

  const isConnected = statut?.pret || statut?.isReady;
  const membreSelectionneInfo = membres.find(m => String(m.id) === String(membreSelectionne));

  const membresFiltresAbonnes = membresAvecTel.filter(m => {
    const q = searchAbonnes.toLowerCase();
    return !q || `${m.prenom} ${m.nom}`.toLowerCase().includes(q) || (m.telephone || '').includes(q);
  });

  const nbChangements = Object.keys(pendingChanges).length;

  const membresFiltresAnniv = abonnesAnniversaires.filter(m => {
    const q = searchAbonnesAnniv.toLowerCase();
    return !q || `${m.prenom} ${m.nom}`.toLowerCase().includes(q) || (m.telephone || '').includes(q);
  });
  const nbChangementsAnniv = Object.keys(pendingChangesAnniv).length;

  return (
    <Box maxW="900px" mx="auto">
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">📱 WhatsApp EECF</Heading>
        <Badge colorScheme={isConnected ? 'green' : 'red'} fontSize="md" px={3} py={1} borderRadius="md">
          {isConnected ? '✅ Connecté' : '❌ Déconnecté'}
        </Badge>
      </Flex>

      {/* Connexion */}
      <Box bg="white" p={6} borderRadius="md" shadow="sm" mb={6}>
        <Text fontWeight="bold" mb={4}>Connexion WhatsApp</Text>
        {loadingStatut && <Flex justify="center" py={4}><Spinner /></Flex>}

        {!loadingStatut && !isConnected && (
          <VStack spacing={4} align="stretch">
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              Scannez le QR code avec le WhatsApp du numéro <strong>&nbsp;+33 6 63 59 70 04</strong>
            </Alert>
            <Flex justify="center" direction="column" align="center" gap={3}>
              {loadingQr && <Spinner size="xl" color="green.500" />}
              {!loadingQr && qrCode && (
                <>
                  <Image src={qrCode} alt="QR Code WhatsApp" maxW="280px" border="4px solid" borderColor="green.500" borderRadius="md" bg="white" p={2} />
                  <Text fontSize="xs" color="gray.500">Se rafraîchit automatiquement toutes les 15 secondes</Text>
                </>
              )}
              {!loadingQr && !qrCode && (
                <Alert status="info" borderRadius="md"><AlertIcon />Service WhatsApp en cours de démarrage…</Alert>
              )}
              <Button size="sm" variant="outline" colorScheme="green" onClick={fetchQrCode} isLoading={loadingQr}>
                🔄 Rafraîchir le QR code
              </Button>
            </Flex>
          </VStack>
        )}

        {!loadingStatut && isConnected && (
          <HStack justify="space-between">
            <Alert status="success" borderRadius="md" flex={1}>
              <AlertIcon />WhatsApp connecté et prêt à envoyer des messages.
            </Alert>
            <Button colorScheme="red" variant="outline" size="sm" ml={4} onClick={handleDeconnecter}>Déconnecter</Button>
          </HStack>
        )}
      </Box>

      {/* Tabs */}
      <Tabs colorScheme="green" bg="white" borderRadius="md" shadow="sm">
        <TabList px={4} pt={4}>
          <Tab>📢 Diffusion ({abonnes.length} abonnés)</Tab>
          <Tab>💬 Message individuel</Tab>
          <Tab>👥 Gérer les abonnés</Tab>
          <Tab>🎂 Anniversaires ({anniversairesDuJour.length} aujourd'hui)</Tab>
        </TabList>

        <TabPanels>
          {/* Diffusion */}
          <TabPanel>
            <Text fontSize="sm" color="gray.600" mb={5}>
              Envoyez un message à tous les <strong>{abonnes.length} abonné(s)</strong> aux lectures bibliques WhatsApp.
            </Text>

            <RadioGroup value={modeDiffusion} onChange={(val) => { setModeDiffusion(val); setMessage(''); setLectureJour(null); }} mb={5}>
              <Stack direction="row" spacing={6}>
                <Radio value="lecture" colorScheme="green">📖 Lecture biblique du jour</Radio>
                <Radio value="libre" colorScheme="green">✉️ Message libre</Radio>
              </Stack>
            </RadioGroup>

            <Divider mb={5} />

            {modeDiffusion === 'lecture' ? (
              <VStack align="stretch" spacing={4}>
                <Button
                  colorScheme="green" variant="outline"
                  onClick={fetchLectureJour}
                  isLoading={loadingLecture}
                  leftIcon={<span>📖</span>}
                  alignSelf="flex-start"
                >
                  Charger la lecture du jour
                </Button>

                {lectureJour && (
                  <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <Text fontSize="sm" fontWeight="bold">{lectureJour.reference}</Text>
                      {!lectureJour.is_published && (
                        <Text fontSize="xs" color="orange.600">⚠️ Cette lecture n'a pas encore été publiée sur le blog</Text>
                      )}
                    </Box>
                  </Alert>
                )}

                {message ? (
                  <>
                    <Textarea
                      value={message}
                      onChange={e => setMessage(e.target.value)}
                      rows={8} fontFamily="mono" fontSize="sm"
                    />
                    <Button colorScheme="green" onClick={handleEnvoyerLecture} isLoading={sending}
                      isDisabled={!message.trim() || !isConnected || abonnes.length === 0} leftIcon={<span>📤</span>}
                      alignSelf="flex-start">
                      Envoyer à {abonnes.length} abonné(s)
                    </Button>
                  </>
                ) : (
                  !loadingLecture && (
                    <Text fontSize="sm" color="gray.400" fontStyle="italic">
                      Cliquez sur "Charger la lecture du jour" pour pré-remplir le message.
                    </Text>
                  )
                )}
              </VStack>
            ) : (
              <VStack align="stretch" spacing={4}>
                <Textarea
                  value={messageLibre}
                  onChange={e => setMessageLibre(e.target.value)}
                  placeholder={'✉️ *Message de l\'EECF Paris*\n\nBonjour à tous,\n\n...'}
                  rows={8} fontFamily="mono" fontSize="sm"
                />
                <Button colorScheme="blue" onClick={handleEnvoyerMessageLibre} isLoading={sending}
                  isDisabled={!messageLibre.trim() || !isConnected || abonnes.length === 0} leftIcon={<span>📤</span>}
                  alignSelf="flex-start">
                  Envoyer à {abonnes.length} abonné(s)
                </Button>
              </VStack>
            )}

            {!isConnected && <Text fontSize="xs" color="red.500" mt={3}>WhatsApp doit être connecté pour envoyer des messages.</Text>}
          </TabPanel>

          {/* Message individuel */}
          <TabPanel>
            <Text fontSize="sm" color="gray.600" mb={4}>Envoyez un message WhatsApp à un membre spécifique.</Text>
            <Box mb={4}>
              <Text fontSize="sm" fontWeight="medium" mb={2}>Sélectionner un membre</Text>
              <MemberSelect members={membres} value={membreSelectionne} onChange={id => setMembreSelectionne(id)} placeholder="Rechercher par nom, prénom ou matricule..." />
              {membreSelectionneInfo && (
                <HStack mt={2} spacing={2}>
                  <Badge colorScheme="blue">{membreSelectionneInfo.prenom} {membreSelectionneInfo.nom}</Badge>
                  <Badge colorScheme="gray">{membreSelectionneInfo.telephone}</Badge>
                </HStack>
              )}
            </Box>
            <Textarea value={messageIndividuel} onChange={e => setMessageIndividuel(e.target.value)}
              placeholder="Votre message WhatsApp..." rows={6} mb={4} fontFamily="mono" fontSize="sm" />
            <Button colorScheme="green" onClick={handleEnvoyerIndividuel} isLoading={sendingIndividuel}
              isDisabled={!messageIndividuel.trim() || !membreSelectionne || !isConnected} leftIcon={<span>📨</span>}>
              Envoyer le message
            </Button>
            {!isConnected && <Text fontSize="xs" color="red.500" mt={2}>WhatsApp doit être connecté pour envoyer des messages.</Text>}
          </TabPanel>

          {/* Gérer abonnés */}
          <TabPanel>
            <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={2}>
              <Text fontSize="sm" color="gray.600">
                Activez ou désactivez les lectures bibliques WhatsApp pour chaque membre.
              </Text>
              <HStack>
                {nbChangements > 0 && (
                  <Badge colorScheme="orange">{nbChangements} modification(s)</Badge>
                )}
                <Button size="sm" colorScheme="green" onClick={handleSaveAbonnements}
                  isLoading={savingAbonnements} isDisabled={nbChangements === 0}>
                  💾 Enregistrer
                </Button>
              </HStack>
            </Flex>

            <InputGroup mb={3}>
              <InputLeftElement pointerEvents="none"><SearchIcon color="gray.400" /></InputLeftElement>
              <Input placeholder="Rechercher un membre..." value={searchAbonnes} onChange={e => setSearchAbonnes(e.target.value)} size="sm" />
            </InputGroup>

            <HStack mb={3} spacing={3}>
              <Button size="xs" variant="outline" colorScheme="green" onClick={() => handleToutCocher(true)}>
                ✅ Tout abonner
              </Button>
              <Button size="xs" variant="outline" colorScheme="red" onClick={() => handleToutCocher(false)}>
                ❌ Tout désabonner
              </Button>
              <Text fontSize="xs" color="gray.500">({membresFiltresAbonnes.length} membres avec téléphone)</Text>
            </HStack>

            {membresAvecTel.length === 0 ? (
              <Text color="gray.500" fontSize="sm">Aucun membre avec numéro de téléphone.</Text>
            ) : (
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>Nom</Th>
                      <Th>Téléphone</Th>
                      <Th textAlign="center">Abonné</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {membresFiltresAbonnes.map(m => {
                      const actif = getActif(m);
                      const modifie = m.id in pendingChanges;
                      return (
                        <Tr key={m.id} bg={modifie ? 'orange.50' : undefined}>
                          <Td fontWeight={modifie ? 'semibold' : 'normal'}>
                            {m.prenom} {m.nom}
                          </Td>
                          <Td color="gray.600">{m.telephone}</Td>
                          <Td textAlign="center">
                            <Switch
                              colorScheme="green"
                              isChecked={actif}
                              onChange={e => handleToggle(m.id, e.target.checked)}
                            />
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            )}
          </TabPanel>

          {/* Anniversaires */}
          <TabPanel>
            <Alert status="info" borderRadius="md" mb={5}>
              <AlertIcon />
              <Box fontSize="sm">
                Un message de bon anniversaire est envoyé <strong>automatiquement chaque jour à 8h00</strong> aux membres
                nés ce jour-là et abonnés. Chaque membre ne reçoit qu'un seul message par anniversaire.
              </Box>
            </Alert>

            <Box mb={6}>
              <Text fontWeight="bold" mb={2}>Aperçu du message</Text>
              <Textarea value={apercuMessageAnniv} isReadOnly rows={6} fontFamily="mono" fontSize="sm" bg="gray.50" />
            </Box>

            <Box mb={6}>
              <Flex justify="space-between" align="center" mb={3} flexWrap="wrap" gap={2}>
                <Text fontWeight="bold">🎉 Anniversaires du jour ({anniversairesDuJour.length})</Text>
                <Button
                  size="sm" colorScheme="green" onClick={handleEnvoyerAnniversairesMaintenant}
                  isLoading={envoyantAnniv} isDisabled={!isConnected || anniversairesDuJour.length === 0}
                  leftIcon={<span>📤</span>}
                >
                  Envoyer maintenant
                </Button>
              </Flex>

              {anniversairesDuJour.length === 0 ? (
                <Text color="gray.500" fontSize="sm">Aucun membre n'a d'anniversaire aujourd'hui.</Text>
              ) : (
                <VStack align="stretch" spacing={2}>
                  {anniversairesDuJour.map(m => (
                    <HStack key={m.id} justify="space-between" p={2} bg="gray.50" borderRadius="md">
                      <Text fontSize="sm">{m.prenom} {m.nom}</Text>
                      <Badge colorScheme={m.recevoir_anniversaire_whatsapp ? 'green' : 'gray'}>
                        {m.recevoir_anniversaire_whatsapp ? 'Abonné' : 'Non abonné'}
                      </Badge>
                    </HStack>
                  ))}
                </VStack>
              )}
              {!isConnected && <Text fontSize="xs" color="red.500" mt={2}>WhatsApp doit être connecté pour envoyer des messages.</Text>}
            </Box>

            <Divider mb={5} />

            <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={2}>
              <Text fontSize="sm" color="gray.600">
                Activez ou désactivez les messages d'anniversaire WhatsApp pour chaque membre.
              </Text>
              <HStack>
                {nbChangementsAnniv > 0 && (
                  <Badge colorScheme="orange">{nbChangementsAnniv} modification(s)</Badge>
                )}
                <Button size="sm" colorScheme="green" onClick={handleSaveAbonnementsAnniv}
                  isLoading={savingAbonnementsAnniv} isDisabled={nbChangementsAnniv === 0}>
                  💾 Enregistrer
                </Button>
              </HStack>
            </Flex>

            <InputGroup mb={3}>
              <InputLeftElement pointerEvents="none"><SearchIcon color="gray.400" /></InputLeftElement>
              <Input placeholder="Rechercher un membre..." value={searchAbonnesAnniv} onChange={e => setSearchAbonnesAnniv(e.target.value)} size="sm" />
            </InputGroup>

            <HStack mb={3} spacing={3}>
              <Button size="xs" variant="outline" colorScheme="green" onClick={() => handleToutCocherAnniv(true)}>
                ✅ Tout abonner
              </Button>
              <Button size="xs" variant="outline" colorScheme="red" onClick={() => handleToutCocherAnniv(false)}>
                ❌ Tout désabonner
              </Button>
              <Text fontSize="xs" color="gray.500">({membresFiltresAnniv.length} membres)</Text>
            </HStack>

            {abonnesAnniversaires.length === 0 ? (
              <Text color="gray.500" fontSize="sm">Aucun membre actif.</Text>
            ) : (
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>Nom</Th>
                      <Th>Date de naissance</Th>
                      <Th textAlign="center">Abonné</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {membresFiltresAnniv.map(m => {
                      const actif = getActifAnniv(m);
                      const modifie = m.id in pendingChangesAnniv;
                      return (
                        <Tr key={m.id} bg={modifie ? 'orange.50' : undefined}>
                          <Td fontWeight={modifie ? 'semibold' : 'normal'}>
                            {m.prenom} {m.nom}
                          </Td>
                          <Td color="gray.600">
                            {m.date_naissance ? new Date(m.date_naissance).toLocaleDateString('fr-FR') : '—'}
                          </Td>
                          <Td textAlign="center">
                            <Switch
                              colorScheme="green"
                              isChecked={actif}
                              onChange={e => handleToggleAnniv(m.id, e.target.checked)}
                            />
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}
